import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * Rows are not always shown in Shopify's line order (the free gift is moved
 * to second place by snippets/cart-products.liquid), so rows are looked up
 * by their data-line attribute (Shopify's 1-based line number) instead of
 * their position in the list.
 *
 * Free gift: when the customer removes the LAST subscription line (delete
 * icon, or quantity down to 0), the free gift line is removed in the same
 * request. Otherwise snippets/free-gift-guard would have to remove the gift
 * afterwards and reload the page.
 *
 * Rows are always read from what is actually on the page (see #rows). The
 * base component only refreshes its list of rows when something is ADDED to
 * the cart list, not when a row is only taken away, so after a removal the
 * list could still hold the row that was just removed. That stale row made
 * the cart think a second subscription was still there, so the gift was
 * not removed with the last subscription.
 *
 * Quantity changes stay responsive:
 * - the request goes out 150ms after the last click (was 300ms);
 * - ordinary lines skip the subscription app's extra cart reads (see #changeUrl);
 * - the - / + buttons keep working while a request is running. The latest
 *   quantity is remembered and sent as soon as the running request is back,
 *   and the cart is only redrawn once, with the final result;
 * - the cart sent back by Shopify is passed on with the update event, so
 *   other scripts (free-gift-guard) don't have to read the cart again;
 * - each update logs its timing to the console as "[cart] quantity update".
 *
 * Rows of the same product (same key) are changed together by key, and a
 * removed row is only taken off the screen if the redraw didn't reuse it
 * (see updateQuantity and onLineItemRemove).
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 150).bind(this);

  /** A cart request is running. */
  #inFlight = false;

  /**
   * Quantities chosen while a request was running, by line number.
   * @type {Map<number, number>}
   */
  #queued = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Cart rows that are really on the page right now. Rows that a redraw or
   * a remove animation has already taken off the page are left out.
   * @returns {HTMLTableRowElement[]}
   */
  #rows() {
    return (this.refs.cartItemRows || []).filter((row) => row.isConnected && this.contains(row));
  }

  /**
   * Finds the row for a Shopify cart line, whatever its display position.
   * @param {number} line - The 1-based line number.
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line) {
    const rows = this.#rows();
    return rows.find((row) => row.dataset.line === String(line)) || rows[line - 1];
  }

  /**
   * Line keys in Shopify's line order, as currently shown.
   * @returns {string[]}
   */
  #shownLineKeys() {
    const rows = this.#rows().filter((row) => row.dataset.line);
    rows.sort((a, b) => Number(a.dataset.line) - Number(b.dataset.line));
    return rows.map((row) => row.dataset.key || '');
  }

  /**
   * Free gift rows that should go together with this row, if this row is
   * the last subscription line in the cart. Empty otherwise. Rows already
   * on their way out (fading) don't count as another subscription.
   * @param {HTMLTableRowElement | undefined} row
   * @returns {HTMLTableRowElement[]}
   */
  #giftRowsToRemoveWith(row) {
    if (!row || !row.classList.contains('cart-items__table-row--sub')) return [];

    const rows = this.#rows();
    const otherSubscriptions = rows.filter(
      (other) =>
        other !== row &&
        !other.classList.contains('removing') &&
        other.classList.contains('cart-items__table-row--sub')
    );
    if (otherSubscriptions.length > 0) return [];

    return rows.filter((other) => other.classList.contains('cart-items__table-row--gift'));
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const lineItemRow = this.#rowForLine(line);
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();

    if (this.#inFlight) {
      // Send it as soon as the running request is back.
      this.#queued.set(line, quantity);
      return;
    }

    this.#applyQuantity(line, quantity);
  }

  /**
   * @param {number} line
   * @param {number} quantity
   */
  #applyQuantity(line, quantity) {
    if (quantity === 0) {
      this.onLineItemRemove(line);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * After a request: the next queued quantity that still differs from the
   * cart Shopify sent back, or null. Queued quantities are dropped if the
   * lines moved (a line was added, removed or split), because their line
   * numbers may no longer point at the same product.
   * @param {any} cart - Cart JSON from the response.
   * @returns {{ line: number, quantity: number } | null}
   */
  #takeQueued(cart) {
    if (this.#queued.size === 0) return null;

    const items = Array.isArray(cart?.items) ? cart.items : null;
    const shownKeys = this.#shownLineKeys();
    const sameLines =
      items &&
      items.length === shownKeys.length &&
      items.every((/** @type {any} */ item, /** @type {number} */ index) => item.key === shownKeys[index]);

    if (!sameLines) {
      this.#queued.clear();
      return null;
    }

    for (const [line, quantity] of this.#queued) {
      this.#queued.delete(line);
      const item = items[line - 1];
      if (item && item.quantity !== quantity) return { line, quantity };
    }

    return null;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#rowForLine(line);
    const giftRows = this.#giftRowsToRemoveWith(cartItemRowToRemove);

    if (cartItemRowToRemove && giftRows.length > 0) {
      // Last subscription goes: take the free gift out in the same request.
      const updates = {};
      [cartItemRowToRemove, ...giftRows].forEach((row) => {
        if (row.dataset.key) updates[row.dataset.key] = 0;
      });
      this.#sendCartRequest({
        url: (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js',
        payload: { updates },
        line,
        action: 'clear',
      });
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...giftRows,
      // Get all nested lines of the row to remove
      ...this.#rows().filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      // The cart can be redrawn from Shopify's answer while this row is
      // still fading out, and that redraw may reuse this very row for a line
      // that stays in the cart (it loses its "removing" class then). Only
      // take the row away if it is still the row being removed -- otherwise
      // a line that is still in the cart would vanish from the screen.
      const remove = () => {
        if (prefersReducedMotion() || row.classList.contains('removing')) row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const { line, quantity, action } = config;

    // Shopify can show one product as two rows (for example when the free
    // gift discount covers only part of the quantity). Both rows share one
    // key, and Shopify treats them as one item. Changing one of them by its
    // row number can then change or remove BOTH rows. So for such rows the
    // request names the item by its key and gives the new total: this row's
    // new quantity plus what the other row(s) already hold.
    const row = this.#rowForLine(line);
    const otherRowsQuantity = this.#sameItemQuantity(row);
    const payload =
      row?.dataset.key && otherRowsQuantity !== null
        ? { id: row.dataset.key, quantity: otherRowsQuantity + quantity }
        : { line, quantity };

    this.#sendCartRequest({
      url: this.#changeUrl(line),
      payload,
      line,
      action,
    });
  }

  /**
   * Total quantity of the OTHER rows that show the same cart item (same
   * key) as this row, or null if this row is the only one.
   * @param {HTMLTableRowElement | undefined} row
   * @returns {number | null}
   */
  #sameItemQuantity(row) {
    const key = row?.dataset.key;
    if (!key) return null;

    const others = this.#rows().filter(
      (other) => other !== row && other.dataset.key === key && !other.classList.contains('removing')
    );
    if (others.length === 0) return null;

    return others.reduce((sum, other) => {
      const input = /** @type {HTMLInputElement | null} */ (other.querySelector('.cart-items__quantity input'));
      return sum + (parseInt(input?.defaultValue || '0', 10) || 0);
    }, 0);
  }

  /**
   * Which cart/change address to use for a line.
   *
   * The subscription app (Super Simple Subscriptions) checks every
   * "/cart/change" request first: it reads the whole cart before letting
   * the request go out, and again afterwards. That check only matters for
   * lines it manages itself (bundles, bulk-quantity plans -- recognisable by
   * their hidden line properties). It leaves "/cart/change.js" alone, so
   * ordinary lines use that and skip both extra cart reads. The lines the
   * app manages are listed by snippets/cart-items-layout.liquid
   * (data-app-lines); if that list is missing, the app checks as before.
   * @param {number} line - The 1-based line number.
   * @returns {string}
   */
  #changeUrl(line) {
    const base = `${Theme.routes.cart_change_url}`;
    const marker = this.querySelector('[data-app-lines]');
    if (!marker) return base;

    const appLines = (marker.getAttribute('data-app-lines') || '').split(',').filter(Boolean);
    if (appLines.includes(String(line))) return base;

    return base.endsWith('.js') ? base : `${base}.js`;
  }

  /**
   * Sends a cart change/update request and re-renders the cart sections
   * from the response (no page reload).
   * @param {Object} config
   * @param {string} config.url - cart/change or cart/update endpoint.
   * @param {Object} config.payload - Request body (line/quantity or updates).
   * @param {number} config.line - The line the customer acted on (for errors).
   * @param {string} config.action - 'change' or 'clear'.
   */
  #sendCartRequest(config) {
    const { url, payload, line, action } = config;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const startedAt = performance.now();
    let serverTiming = '';

    this.#inFlight = true;
    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...payload,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    /** @type {{ line: number, quantity: number } | null} */
    let next = null;

    fetch(url, fetchConfig('json', { body }))
      .then((response) => {
        serverTiming = response.headers.get('server-timing') || '';
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        const respondedAt = performance.now();

        if (parsedResponseText.errors) {
          this.#queued.clear();
          resetShimmer(this);
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // A newer quantity was chosen while this request ran: send that
        // instead of drawing this (already outdated) result.
        next = this.#takeQueued(parsedResponseText);
        if (next) return;

        resetShimmer(this);

        const { sections, ...cart } = parsedResponseText;

        const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections,
            cart: Array.isArray(cart.items) ? cart : undefined,
          })
        );

        morphSection(this.sectionId, sections[this.sectionId]);

        this.#logTiming(action, startedAt, respondedAt, serverTiming);
      })
      .catch((error) => {
        this.#queued.clear();
        console.error(error);
      })
      .finally(() => {
        this.#inFlight = false;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);

        if (next) this.#applyQuantity(next.line, next.quantity);
      });
  }

  /**
   * Console timing for a cart update, to see where the time goes:
   * server = Shopify's own processing time (cart change, discounts, apps,
   * rendering the cart sections), network = the rest of the round trip,
   * redraw = updating the page.
   * @param {string} action
   * @param {number} startedAt
   * @param {number} respondedAt
   * @param {string} serverTiming - Server-Timing response header.
   */
  #logTiming(action, startedAt, respondedAt, serverTiming) {
    try {
      const total = Math.round(respondedAt - startedAt);
      const match = /processing;dur=([\d.]+)/.exec(serverTiming);
      const server = match ? Math.round(parseFloat(match[1])) : null;
      requestAnimationFrame(() => {
        console.info('[cart] quantity update', {
          action,
          request_ms: total,
          server_ms: server,
          network_ms: server === null ? null : Math.max(0, total - server),
          redraw_ms: Math.round(performance.now() - respondedAt),
        });
      });
    } catch (e) {}
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const row = this.#rowForLine(line);
    const quantityInput = row?.querySelector('.cart-items__quantity input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
